import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';

import { DataGridPro } from '@mui/x-data-grid-pro';

import { Box, IconButton, Tooltip } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import CustomToolbar from './utils/CustomToolbar';
import NoDataOverlay from './utils/NoDataOverlay';

import UseGridState from './hooks/UseGridState';
import { processClientRows } from './utils/FilterHelper';
import ColumnSearchHeader from './utils/ColumnSearchHeader';
import useGridLayoutSync from './utils/useGridLayoutSync';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Supported column properties:
//
// field                : string (required)
// headerName           : string (required)
//
// type                 : 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'currency' | 'email'
//
// width                : number
// flex                 : number
//
// sortable             : boolean
// searchable           : boolean
// filterable           : boolean
// hideable             : boolean
// hide                 : boolean
// internal             : boolean
//
// align                : 'left' | 'center' | 'right'
// headerAlign          : 'left' | 'center' | 'right'
//
// valueFormatter       : function
// renderCell           : function
const DEFAULT_COLUMNS = [];

const DEFAULT_TOOLBAR = {
  showSearch: true,
  showColumnSelector: true,
  showFilter: true,
  showDensity: true,
  showExport: true,
  showPrint: true,
  printTitle: 'Records',
};

const DEFAULT_SEARCH = {
  placeholder: 'Search...',
};

const DEFAULT_PAGINATION = {
  enabled: true,
  pageSize: 25,
  rowsPerPageOptions: [10, 25, 50, 100],
};

const DEFAULT_INITIAL_PINNED_COLUMNS = {
  left: [],
  right: [],
};

const DEFAULT_PERSIST_GRID_STATE = {
  pageSize: false,
  density: false,
  columnVisibility: false,
  columnWidths: false,
  sortModel: false,
  searchText: false,
  columnSearch: false,
  filters: false,
  pinnedColumns: false,
};

const DEFAULT_SERVER_SIDE = {
  totalRecords: 0,
  onStateChange: null,
};

const DEFAULT_GET_ROW_CLASS_NAME = () => '';

const DEFAULT_ACTIONS = {
  edit: {
    visible: false,
    onClick: null,
    disabled: false,
  },
  delete: {
    visible: false,
    onClick: null,
    disabled: false,
  },
  custom: [],
};

const DEFAULT_LAYOUT_SYNC = {
  enabled: false,
  gridKey: null,
};

const DEFAULT_SELECTION_BAR = {
  enabled: false,
  actions: [],
};

const CommonDataGrid = ({
  rows = [],
  columns = DEFAULT_COLUMNS,

  loading = false,
  className = '',
  mode = 'client',

  toolbar = DEFAULT_TOOLBAR,

  search = DEFAULT_SEARCH,

  enableColumnSearch = false,
  disableColumnReorder = true,

  pagination = DEFAULT_PAGINATION,

  initialPinnedColumns = DEFAULT_INITIAL_PINNED_COLUMNS,

  checkboxSelection = false,

  disableColumnMenu = false,
  disableColumnResize = true,

  persistGridState = DEFAULT_PERSIST_GRID_STATE,
  storageKey = 'common-grid',

  serverSide = DEFAULT_SERVER_SIDE,

  onRowClick,
  getRowId,
  getRowClassName = DEFAULT_GET_ROW_CLASS_NAME,
  actions = DEFAULT_ACTIONS,
  layoutSync = DEFAULT_LAYOUT_SYNC,
  selectionBar = DEFAULT_SELECTION_BAR,
  exportActions = [],
  onSelectionChange,
  ...otherProps
}) => {
  const {
    pageSize,
    setPageSize,

    density,
    setDensity,

    columnVisibilityModel,
    setColumnVisibilityModel,

    columnWidths,
    setColumnWidths,

    pinnedColumns,
    setPinnedColumns,

    sortModel,
    setSortModel,

    searchText,
    setSearchText,

    columnSearch,
    setColumnSearch,

    filters,
    setFilters,

    columnOrder,
    setColumnOrder,
  } = UseGridState({
    storageKey,
    persistGridState,
    defaultPageSize: pagination.pageSize,
  });

  const gridStateForSync = useMemo(
    () => ({
      columnOrder,
      setColumnOrder,
      columnVisibilityModel,
      setColumnVisibilityModel,
      columnWidths,
      setColumnWidths,
      pinnedColumns,
      setPinnedColumns,
      sortModel,
      setSortModel,
      density,
      setDensity,
      pageSize,
      setPageSize,
    }),
    [
      columnOrder,
      setColumnOrder,
      columnVisibilityModel,
      setColumnVisibilityModel,
      columnWidths,
      setColumnWidths,
      pinnedColumns,
      setPinnedColumns,
      sortModel,
      setSortModel,
      density,
      setDensity,
      pageSize,
      setPageSize,
    ]
  );

  const { loaded: layoutLoaded, saving: layoutSaving, saveLayout, resetLayout } = useGridLayoutSync(
    {
      enabled: layoutSync.enabled,
      gridKey: layoutSync.gridKey,
      gridState: gridStateForSync,
      defaultPageSize: pagination.pageSize,
    }
  );

  const [searchInput, setSearchInput] = useState(searchText);

  const [localColumnSearch, setLocalColumnSearch] = useState(columnSearch);

  const [page, setPage] = useState(0);

  const [selectionModel, setSelectionModel] = useState([]);
  const [reorderEnabled, setReorderEnabled] = useState(!disableColumnReorder);

  const hasActions =
    actions?.edit?.visible ||
    actions?.delete?.visible ||
    actions?.custom?.some((action) => action.visible !== false);

  const handleSearch = useCallback(
    (value = searchInput) => {
      setSearchText(value);
    },
    [searchInput, setSearchText]
  );

  const handleColumnWidthChange = useCallback(
    (params) => {
      setColumnWidths((prev) => ({
        ...prev,
        [params.colDef.field]: params.width,
      }));
    },
    [setColumnWidths]
  );

  const handleColumnSearchChange = useCallback((field, value) => {
    setLocalColumnSearch((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchText, columnSearch]);

  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setColumnSearch(localColumnSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [localColumnSearch, setColumnSearch]);

  const actionColumn = useMemo(
    () => ({
      field: 'actions',
      headerName: 'Actions',
      fixed: 'right',
      minWidth: 150,
      sortable: false,
      filterable: false,
      hideable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,

      renderCell: (params) => (
        <>
          {actions?.edit?.visible && (
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  disabled={actions.edit.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    actions.edit.onClick?.(params.row);
                  }}>
                  <EditIcon color="primary" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {actions?.delete?.visible && (
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  disabled={actions.delete.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    actions.delete.onClick?.(params.row);
                  }}>
                  <DeleteIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {actions?.custom
            ?.filter((action) => action.visible !== false)
            .map((action, index) => (
              <Tooltip key={index} title={action.tooltip}>
                <span>
                  <IconButton
                    size="small"
                    disabled={action.disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick?.(params.row);
                    }}>
                    {action.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
        </>
      ),
    }),
    [actions]
  );

  const finalColumns = useMemo(() => {
    const visibleColumns = columns
      .filter((column) => column.internal !== true)
      .map((column) => ({
        ...column,

        width: columnWidths[column.field] ?? column.width,

        fixed: column.fixed,

        minWidth: column.minWidth ?? 100,

        align: column.align || 'center',
        headerAlign: column.headerAlign || 'center',

        sortable: column.sortable !== false,
        filterable: column.filterable !== false,

        hideable: column.hideable,

        disableColumnMenu: column.disableColumnMenu ?? disableColumnMenu,

        disableColumnSelector: column.disableColumnSelector,
      }));

    return hasActions ? [...visibleColumns, actionColumn] : visibleColumns;
  }, [columns, columnWidths, disableColumnMenu, hasActions, actionColumn]);

  const searchableColumns = useMemo(() => {
    return finalColumns.map((column) => {
      const isSearchable =
        enableColumnSearch && column.searchable !== false && column.field !== 'actions';
      const isDraggable = reorderEnabled && column.field !== 'actions';
      return {
        ...column,

        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
            {isDraggable && (
              <DragIndicatorIcon
                fontSize="small"
                sx={{ color: 'action.disabled', cursor: 'grab', flexShrink: 0 }}
              />
            )}
            <ColumnSearchHeader
              column={column}
              enableColumnSearch={enableColumnSearch}
              isSearchable={isSearchable}
              value={localColumnSearch[column.field] || ''}
              onChange={(e) => handleColumnSearchChange(column.field, e.target.value)}
            />
          </Box>
        ),
      };
    });
  }, [
    finalColumns,
    enableColumnSearch,
    localColumnSearch,
    handleColumnSearchChange,
    reorderEnabled,
  ]);

  const orderedColumns = useMemo(() => {
    const order = Array.isArray(columnOrder) ? columnOrder : [];

    if (order.length === 0) {
      return searchableColumns;
    }

    const columnMap = new Map(searchableColumns.map((col) => [col.field, col]));
    const ordered = order.map((field) => columnMap.get(field)).filter(Boolean);
    const remaining = searchableColumns.filter((col) => !order.includes(col.field));

    return [...ordered, ...remaining];
  }, [searchableColumns, columnOrder]);

  const handleColumnOrderChange = useCallback(
    (params) => {
      setColumnOrder((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const currentOrder = safePrev.length ? safePrev : orderedColumns.map((c) => c.field);
        const newOrder = [...currentOrder];
        const fromIndex = newOrder.indexOf(params.field);

        if (fromIndex !== -1) newOrder.splice(fromIndex, 1);

        const adjustedTargetIndex = checkboxSelection
          ? Math.max(0, params.targetIndex - 1)
          : params.targetIndex;

        newOrder.splice(adjustedTargetIndex, 0, params.field);

        return newOrder;
      });
    },
    [orderedColumns, setColumnOrder, checkboxSelection]
  );

  const defaultPinnedColumns = useMemo(() => {
    const left = [...(initialPinnedColumns.left || [])];

    const right = [...(initialPinnedColumns.right || [])];

    if (checkboxSelection && !left.includes('__check__')) {
      left.unshift('__check__');
    }

    searchableColumns.forEach((col) => {
      if (col.fixed === 'left' && !left.includes(col.field)) {
        left.push(col.field);
      }

      if (col.fixed === 'right' && col.field !== 'actions' && !right.includes(col.field)) {
        right.push(col.field);
      }
    });

    if (hasActions && !right.includes('actions')) {
      right.push('actions');
    }

    return {
      left,
      right,
    };
  }, [searchableColumns, checkboxSelection, hasActions, initialPinnedColumns]);

  const processedRows = useMemo(
    () =>
      processClientRows({
        rows,
        searchText,
        columnSearch,
      }),
    [rows, searchText, columnSearch]
  );

  useEffect(() => {
    onSelectionChange?.(processedRows.filter((r) => selectionModel.includes(r.id)));
  }, [selectionModel, processedRows, onSelectionChange]);

  useEffect(() => {
    const isEmpty = pinnedColumns.left?.length === 0 && pinnedColumns.right?.length === 0;

    if (isEmpty) {
      setPinnedColumns(defaultPinnedColumns);
    }
  }, [defaultPinnedColumns, pinnedColumns, setPinnedColumns]);

  const handlePinnedColumnsChange = useCallback(
    (newPinnedColumns) => {
      const defaultRight = initialPinnedColumns?.right || [];

      const userPinnedRight = (newPinnedColumns.right || []).filter(
        (field) => !defaultRight.includes(field) && field !== 'actions'
      );

      const right = [...userPinnedRight, ...defaultRight.filter((field) => field !== 'actions')];

      if (hasActions && !right.includes('actions')) {
        right.push('actions');
      }

      setPinnedColumns({
        ...newPinnedColumns,
        right,
      });
    },
    [initialPinnedColumns, hasActions, setPinnedColumns]
  );

  const lastSyncedDensityRef = useRef(density);

  const handleStateChange = useCallback(
    (state) => {
      const newDensity = state?.density?.value;

      if (newDensity && newDensity !== lastSyncedDensityRef.current) {
        lastSyncedDensityRef.current = newDensity;
        setDensity(newDensity);
      }
    },
    [setDensity]
  );

  const defaultColumnVisibility = useMemo(
    () =>
      columns.reduce((acc, col) => {
        if (col.hide === true) {
          acc[col.field] = false;
        }

        return acc;
      }, {}),
    [columns]
  );

  const mergedColumnVisibilityModel = useMemo(
    () => ({
      ...defaultColumnVisibility,
      ...columnVisibilityModel,
    }),
    [defaultColumnVisibility, columnVisibilityModel]
  );

  const toolbarProps = useMemo(
    () => ({
      toolbar,
      searchInput,
      setSearchInput,
      onSearch: handleSearch,
      search,
      layoutSync: {
        enabled: layoutSync.enabled,
        saving: layoutSaving,
        saveLayout,
        resetLayout,
      },
      columnOrdering: {
        orderedColumns,
        setColumnOrder,
        reorderEnabled,
        toggleReorder: () => setReorderEnabled((prev) => !prev),
      },
      columnVisibility: {
        model: mergedColumnVisibilityModel,
        setModel: setColumnVisibilityModel,
      },
      exportActions,
    }),
    [
      toolbar,
      searchInput,
      handleSearch,
      search,
      layoutSync.enabled,
      layoutSaving,
      saveLayout,
      resetLayout,
      orderedColumns,
      setColumnOrder,
      reorderEnabled,
      mergedColumnVisibilityModel,
      setColumnVisibilityModel,
      exportActions,
    ]
  );

  useEffect(() => {
    if (mode !== 'server' || !serverSide?.onStateChange) {
      return;
    }

    serverSide.onStateChange({
      searchText,
      page,
      pageSize,
      sortModel,
    });
  }, [mode, serverSide?.onStateChange, searchText, page, pageSize, sortModel]);

  const isEmpty = !loading && processedRows.length === 0;
  const showToolbar = toolbar?.showToolbar !== false;

  return (
    <Box className={`common-data-grid-container ${className}`}>
      <DataGridPro
        className={`common-data-grid ${isEmpty ? 'common-data-grid--empty' : ''}`}
        rows={processedRows}
        columns={orderedColumns}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        pinnedColumns={pinnedColumns}
        onColumnOrderChange={handleColumnOrderChange}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        loading={loading || (layoutSync.enabled && !layoutLoaded)}
        headerHeight={enableColumnSearch ? 80 : 50}
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
          const selectedRows = processedRows.filter((row) => newSelection.includes(row.id));
          onSelectionChange?.(selectedRows);
        }}
        disableSelectionOnClick
        onRowClick={onRowClick}
        density={density}
        onStateChange={handleStateChange}
        disableColumnResize={disableColumnResize}
        disableColumnReorder={!reorderEnabled}
        onColumnWidthChange={handleColumnWidthChange}
        columnVisibilityModel={mergedColumnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sortingMode={mode === 'server' ? 'server' : 'client'}
        pagination={pagination.enabled}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        rowsPerPageOptions={pagination.rowsPerPageOptions}
        rowCount={mode === 'server' ? serverSide.totalRecords : processedRows.length}
        paginationMode={mode === 'server' ? 'server' : 'client'}
        filterModel={{
          items: filters,
        }}
        onFilterModelChange={(model) => setFilters(model.items || [])}
        components={{
          Toolbar: showToolbar ? CustomToolbar : null,
          NoRowsOverlay: NoDataOverlay,
        }}
        componentsProps={{
          toolbar: toolbarProps,
        }}
        {...otherProps}
      />
    </Box>
  );
};

export default CommonDataGrid;
